import { BackgroundImage } from "@/components/Primitives/BackgroundImage";
import dynamic from "next/dynamic";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

const AnimatedSection = dynamic(
  () => import("../../components/Animations/useAnimations"),
  { ssr: false }
);
const ConsultationForm = dynamic(
  () => import("../../components/HeroSection/ConsultationForm"),
  { ssr: false }
);
const ScreenAwareContent = dynamic(
  () => import("../../utils/screenAwareContent"),
  {
    ssr: false,
    loading: () => <div className="min-h-screen h-screen" />, // Simple loading state
  }
);

interface HeroSectionData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  buttons?: {
    menu_items_id: {
      href: string;
      label: string;
    };
  }[];
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",

  "subheading.text",
  "subheading.title",

  "text.text",

  "buttons.menu_items_id.href",
  "buttons.menu_items_id.label",

  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
] as const;

interface HeroSectionProps {
  zIndex?: number;
  thresholds?: number[][];
  mobileThresholds?: number[][];
  data?: HeroSectionData;
  transitionTime?: number;
  helperRotation?: number;
}

export default function HeroSection({
  zIndex,
  thresholds,
  mobileThresholds,
  transitionTime,
  helperRotation,
  data,
}: HeroSectionProps) {
  const mobileImage = data?.images?.[0]?.images_id;
  const desktopImage = data?.images?.[1]?.images_id;
  const ctaButton = data?.buttons?.[0]?.menu_items_id;

  // Video URL for background
  const videoUrl = "https://bader.wtgdigital.io/assets/bf816afd-072c-4735-9c09-3f523a9dfbb1";

  return (
		<ScreenAwareContent
			supportedContent={
				<AnimatedSection
					helperRotation={helperRotation}
					className='min-h-screen h-screen overflow-hidden fixed w-full pt-[20rem] lg:pt-[32rem] top-0 left-0'
					thresholds={[
						thresholds?.[0]?.[0] ?? 0,
						thresholds?.[thresholds.length - 1]?.[1] ?? 0,
					]}
					mobileThresholds={[
						mobileThresholds?.[0]?.[0] ?? 0,
						mobileThresholds?.[mobileThresholds.length - 1]?.[1] ?? 0,
					]}
					zIndex={zIndex ?? 0}
					withZIndex={true}
					transitionTime={transitionTime ?? 1400}
					variants={{
						desktop: {
							visible: { y: 0, opacity: 1 },
							above: { y: '100vh', opacity: 1 },
							below: { y: '-100vh', opacity: 1 },
							outside: { y: '-100vh', opacity: 1 },
						},
						mobile: {
							visible: { y: 0, opacity: 1 },
							above: { y: 0, opacity: 1 },
							below: { y: 0, opacity: 1 },
							outside: { y: 0, opacity: 0 },
						},
					}}
					section='section'
					useExtraPosition={true}
				>
					<div className='gl-container h-full'>
						<div className='absolute left-0 top-[70px] inset-0 w-full h-full overflow-hidden'>
							{/* Video Background */}
							<div className='absolute w-full h-full top-0 left-0 inset-0 z-10 bg-[#c7c9ca]'>
								<video
									autoPlay
									muted
									loop
									playsInline
									preload='auto'
									className='absolute top-0 left-0 lg:left-[20%] z-10 inset-0 w-full lg:w-[130%] h-full object-cover object-top'
								>
									<source src={videoUrl} type='video/mp4' />
								</video>

								{/* Darkened overlay */}
								<div className='absolute w-full h-full top-0 left-0 inset-0 bg-primary/60 lg:bg-primary/40 z-20' />
							</div>

							{/* White background on the left side - only for desktop */}
							<div className='absolute inset-0 hidden lg:block bg-white z-15' />

							{/* Original gradient overlay */}
							<div className='absolute inset-0 bg-gradient-to-t from-primary/60 to-60% to-transparent lg:bg-gradient-to-r lg:from-white/90 lg:to-60% lg:to-transparent z-10' />
						</div>
						<AnimatedSection
							mobileThresholds={[
								mobileThresholds?.[0]?.[0] ?? 0,
								mobileThresholds?.[mobileThresholds.length - 1]?.[1] ?? 0,
							]}
							zIndex={zIndex ?? 0}
							transitionTime={transitionTime ?? 1400}
							className='lg:w-[90vw] max-w-[1550px] relative h-full flex flex-col justify-end items-start left-[0] lg:left-[0.2vw] bottom-0 lg:bottom-0 pb-0 lg:pb-12 z-40'
							variants={{
								mobile: {
									visible: { opacity: 1 },
									above: { opacity: 1 },
									below: { opacity: 0 },
								},
							}}
							section='div'
						>
							<div className='w-full lg:w-[95%] lg:max-w-2xl'>
								<div className='mb-6 lg:mb-8 md:max-w-[545px]'>
									<h1 className='text-[2.5rem] font-essonnes font-light leading-tight md:text-[3.625rem] lg:text-[4rem] text-white lg:text-primary mb-4'>
										{data?.heading?.title}
									</h1>
									<div className='hidden lg:block text-primary text-2xl font-greycliff font-medium mb-4'>
										{data?.subheading?.title}
									</div>
									<div
										className='text-white lg:text-primary font-greycliff text-[1rem] lg:text-lg font-normal lg:font-medium leading-normal lg:leading-relaxed tracking-tight'
										dangerouslySetInnerHTML={sanitizeHTML(
											data?.text?.text || ''
										)}
									/>
								</div>
								<div className='flex items-start gap-4 w-full'>
									<div className='lg:hidden flex items-center justify-center gap-4 w-full'>
										<Link
											href={ctaButton?.href || '#'}
											className='w-full px-4 py-3 bg-[#dac79b] rounded-3xl text-center'
										>
											<span className='text-primary font-greycliff text-lg font-normal tracking-tight'>
												{ctaButton?.label}
											</span>
										</Link>
									</div>
								</div>
							</div>
							<div className='mt-4 w-full hidden lg:block'>
								<ConsultationForm />
							</div>
						</AnimatedSection>
					</div>
				</AnimatedSection>
			}
			fallbackContent={
				<section className='relative overflow-hidden xl:py-24 max-[500px]:h-screen'>
					<div className='gl-container'>
						<div className='absolute left-0 top-0 inset-0 w-full overflow-hidden max-[500px]:h-screen'>
							{/* Video Background for fallback */}
							<div className='absolute w-full h-full top-0 left-0 inset-0 z-10'>
								<video
									autoPlay
									muted
									loop
									playsInline
									preload='auto'
									className='absolute top-0 left-0 xl:left-[30%] z-10 inset-0 w-full xl:w-[130%] h-full object-cover object-center'
								>
									<source src={videoUrl} type='video/mp4' />
								</video>

								{/* Darkened overlay */}
								<div className='absolute w-full h-full top-0 left-0 inset-0 bg-primary/60 xl:bg-primary/40 z-20' />
							</div>

							{/* White background on the left side - only for desktop */}
							<div className='absolute inset-0 hidden xl:block bg-white z-25' />

							{/* Original gradient overlay */}
							<div className='absolute inset-0 bg-gradient-to-t from-primary/60 to-60% to-transparent min-[600px]:bg-gradient-to-r min-[600px]:from-white/90 min-[600px]:to-60% min-[600px]:to-transparent z-30' />
						</div>
						<div className='min-[600px]:w-[80vw] max-w-[1550px] relative flex flex-col justify-end items-start left-[0] min-[600px]:left-[0.2vw] md:py-16 max-[500px]:h-[90vh] z-40'>
							<div className='bg-transparent backdrop-blur-none min-[600px]:backdrop-blur-sm rounded-none min-[600px]:rounded-xl w-full min-[600px]:w-[80%] min-[600px]:max-w-2xl'>
								<div className='mb-6 min-[600px]:mb-8'>
									<h1 className='text-[2.5rem] font-essonnes font-light leading-tight md:text-[3.625rem] lg:text-[4rem] text-white min-[600px]:text-primary mb-4'>
										{data?.heading?.title}
									</h1>
									<div className='hidden min-[600px]:block text-primary text-2xl font-greycliff font-medium mb-4'>
										{data?.subheading?.title}
									</div>
									<div
										className='text-white min-[600px]:text-primary font-greycliff text-[1rem] min-[600px]:text-lg font-normal min-[600px]:font-medium leading-normal min-[600px]:leading-relaxed tracking-tight'
										dangerouslySetInnerHTML={sanitizeHTML(
											data?.text?.text || ''
										)}
									/>
								</div>
								<div className='flex items-start gap-4 w-full'>
									<div className='flex xl:hidden items-center justify-center gap-4 w-full'>
										<Link
											href={data?.buttons?.[0]?.menu_items_id?.href || '#'}
											className='w-full px-4 py-3 bg-[#dac79b] rounded-3xl text-center'
										>
											<span className='text-primary font-greycliff text-lg font-normal tracking-tight'>
												{data?.buttons?.[0]?.menu_items_id?.label}
											</span>
										</Link>
									</div>
								</div>
							</div>
							<div className='mt-4 w-full hidden lg:block'>
								<ConsultationForm />
							</div>
						</div>
					</div>
				</section>
			}
		/>
	)
}
